/**
 * Client-side limits + allow-list for note attachments. Mirrors the
 * server-side rules; the server re-validates everything.
 */

/** Per-file size ceiling (MB), overridable via env for the client pre-check. */
export const MAX_NOTE_ATTACHMENT_SIZE_MB =
    Number(import.meta.env.VITE_NOTE_ATTACHMENT_MAX_SIZE_MB) || 25;
export const MAX_NOTE_ATTACHMENT_SIZE_BYTES = MAX_NOTE_ATTACHMENT_SIZE_MB * 1024 * 1024;
export const MAX_NOTE_ATTACHMENTS_PER_NOTE = 5;

/**
 * Ceiling on the network calls a preview depends on: minting the presigned URL,
 * fetching a text body behind it, and asking the server to convert a document.
 *
 * Load-bearing, not defensive. `apiClient` sets no `timeout`, and axios defaults
 * to `0` — no timeout at all — so a stalled request never settles, the query
 * never leaves `isLoading`, and the preview spinner runs forever. That is the
 * one remaining way to reproduce "the loader continues indefinitely" now that
 * the dialog's own state machine is covered by tests. A bounded failure is
 * recoverable — the dialog offers Download; an unbounded wait is not.
 *
 * Generous rather than tight: the conversion endpoint refuses anything over
 * 15 MB before it starts, so it settles well inside this.
 */
export const NOTE_ATTACHMENT_PREVIEW_TIMEOUT_MS = 20_000;

/**
 * Ceiling on a text file the dialog will pull into the browser to show in a
 * `<pre>`. The whole file is fetched and held as a string, so this is a real
 * memory bound, not a nicety — and it matters more now that the upload cap is
 * 25 MB. Past it the dialog offers the download instead.
 */
export const NOTE_TEXT_PREVIEW_MAX_BYTES = 512 * 1024;

/**
 * Extension → the content type we treat the file as, whatever the browser said.
 * Mirrors `NOTE_ATTACHMENT_EXTENSION_TO_MIME` in
 * `backend/src/modules/inbox/constants/note-attachment.constants.ts`, whose
 * comment explains why `File.type` cannot be trusted here.
 *
 * This copy exists so a bad file is refused instantly instead of after a
 * round-trip. It is NOT what makes the upload correct: the server returns the
 * canonical type it signed the PUT with, and `useNoteAttachmentUpload` sends
 * that back verbatim.
 */
export const NOTE_ATTACHMENT_EXTENSION_TO_MIME: Readonly<Record<string, string>> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv',
    txt: 'text/plain',
    log: 'text/plain',
    md: 'text/markdown',
    markdown: 'text/markdown',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    m4a: 'audio/mp4',
    ogg: 'audio/ogg',
    oga: 'audio/ogg',
    aac: 'audio/aac',
};

/** Lowercased extension of a filename, or '' when it has none. */
function extensionOf(filename: string): string {
    const dot = filename.lastIndexOf('.');
    return dot > 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

/** The content type a picked file should be uploaded as. */
export function canonicalNoteFileType(file: File): string {
    return (
        NOTE_ATTACHMENT_EXTENSION_TO_MIME[extensionOf(file.name)] ??
        file.type.split(';')[0]?.trim().toLowerCase() ??
        ''
    );
}

/**
 * Non-image MIME allow-list (any image is allowed via the `image/` prefix,
 * except SVG). `.doc` and `.xls` are accepted but never previewable — no
 * converter can read the legacy binary formats.
 */
const ALLOWED_NON_IMAGE_MIME_TYPES: readonly string[] = [
    'application/pdf',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'text/csv',
    'text/plain',
    'text/markdown',
    'text/x-markdown',
    'audio/mpeg',
    'audio/wav',
    'audio/x-wav',
    'audio/mp4',
    'audio/ogg',
    'audio/aac',
];

/** SVG can embed script, so it is refused by BOTH name and declared type. */
const SVG_EXTENSION = /\.svgz?$/i;

/**
 * Whether a picked file is an allowed note attachment, judged by its canonical
 * type: any image except SVG, or a member of the allow-set.
 * Mirrors the backend `isAllowedNoteAttachment`.
 */
export function isAllowedNoteFile(file: File): boolean {
    if (SVG_EXTENSION.test(file.name)) return false;
    const type = canonicalNoteFileType(file);
    if (type.startsWith('image/')) return type !== 'image/svg+xml';
    return ALLOWED_NON_IMAGE_MIME_TYPES.includes(type);
}

/**
 * `accept` attribute for the file input (a hint; `isAllowedNoteFile` re-checks).
 * Lists MIME types AND dotted extensions — browsers will not match `.log`,
 * `.md` or `.m4a` on MIME alone because they assign those no type.
 */
export const NOTE_ATTACHMENT_ACCEPT = [
    'image/*',
    ...ALLOWED_NON_IMAGE_MIME_TYPES,
    ...Object.keys(NOTE_ATTACHMENT_EXTENSION_TO_MIME).map((ext) => `.${ext}`),
].join(',');
